import {
  Box,
  Button,
  Dialog,
  DialogContent,
  IconButton,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { ReactElement, useEffect, useState } from 'react';
import Spinner from '../../components/spinner';
import { ROUTES, URN_QDOS_S3 } from '../../helpers/constans';
import { generatePath, useNavigate, useParams } from 'react-router-dom';
import { RootState } from '../../redux/store';
import { useDispatch, useSelector } from 'react-redux';
import { thingQdosActions } from '../../redux/slices/thing-qdos';
import { styles } from './things-qdos-media.styles';
import { shareActions } from '../../redux/slices/share.slice';

const ThingsQdosMedia = (): ReactElement => {
  const { posts } = useSelector((state: RootState) => state.thingsQdos);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { id: itemId } = useParams();

  const [selectedItem, setSelectedItem] = useState<any | null>(null);

  useEffect(() => {
    if (!itemId) return;
    dispatch(thingQdosActions.getAllPostsByItemId(itemId));
  }, [dispatch, itemId]);

  const isCurrentThing = true;
  const qdosEventId = posts?.[0]?.post?.eventId;

  const handleBack = (): void => {
    navigate(ROUTES.things);
  };

  const getMediaUrl = (value?: string): string => {
    if (!value) return '';
    if (value.startsWith('http://') || value.startsWith('https://')) return value;
    return `${URN_QDOS_S3}${value}`;
  };

  const handleAddJoy = (entry: any): void => {
    const post = entry?.post || {};

    const qdosItemId = post?.itemIds?.[0];
    const eventId = post?.eventId;
    const qdosPostId = entry?._id || post?._id;

    if (!qdosItemId || !eventId || !qdosPostId) {
      console.log('JOY CLICK MISSING DATA', {
        qdosItemId,
        eventId,
        qdosPostId,
        entry,
        post,
      });
      return;
    }

    dispatch(
      shareActions.addMemorableMoment({
        qdosItemId,
        qdosEventId: eventId,
        qdosPostId,
        alternativeId: post?.alternativeId,
        mediaType: post?.mediaType,
        imageId: post?.imageId,
        videoUrl: post?.videoUrl,
        description: post?.description,
        eventTitle: post?.eventTitle,
      }),
    );
  };

  const handleAddToShare = (
    qdosItemId?: string,
    eventId?: string,
    shareType?: 'team' | 'fan',
  ): void => {
    if (!qdosItemId || !eventId || !shareType) return;

    dispatch(
      shareActions.addToShare({
        qdosItemId,
        qdosEventId: eventId,
        shareType,
      }),
    );
  };

  const handleViewTeamShare = (): void => {
    if (!qdosEventId) return;
    navigate(generatePath(ROUTES.teamShare, { eventId: qdosEventId }));
  };

  const handleViewFanShare = (): void => {
    if (!qdosEventId) return;
    navigate(generatePath(ROUTES.fanShare, { eventId: qdosEventId }));
  };

  const renderMedia = (entry: any, enlarged = false): ReactElement => {
    const post = entry?.post || {};
    const imageUrl = getMediaUrl(post?.imageId);
    const videoUrl = getMediaUrl(post?.videoUrl);

    if (videoUrl) {
      return (
        <Box
          sx={{
            width: '100%',
            borderRadius: '8px',
            overflow: 'hidden',
            backgroundColor: '#000',
          }}
        >
          <video
            controls
            style={{
              display: 'block',
              width: '100%',
              maxHeight: enlarged ? '75vh' : '260px',
              objectFit: 'contain',
              backgroundColor: '#000',
            }}
          >
            <source src={videoUrl} />
            Your browser does not support the video tag.
          </video>
        </Box>
      );
    }

    if (imageUrl) {
      return (
        <Box
          component="img"
          src={imageUrl}
          alt={post?.eventTitle || 'QDOS media'}
          sx={{
            width: '100%',
            height: enlarged ? 'auto' : '220px',
            maxHeight: enlarged ? '75vh' : '220px',
            objectFit: 'cover',
            borderRadius: '8px',
            display: 'block',
            backgroundColor: '#f5f5f5',
          }}
        />
      );
    }

    return (
      <Box
        sx={{
          width: '100%',
          minHeight: enlarged ? '260px' : '220px',
          borderRadius: '8px',
          border: '1px dashed #bbb',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          p: '1rem',
          color: '#777',
          textAlign: 'center',
          backgroundColor: '#fafafa',
        }}
      >
        <Typography>{post?.alternativeId || 'No media preview available'}</Typography>
      </Box>
    );
  };

  return (
    <Box sx={styles.container}>
      {!isCurrentThing ? (
        <Spinner mt="2rem" />
      ) : (
        <Box sx={styles.mainBox}>
          <Box sx={styles.mainTitleBox}>
            <Box>
              <Typography sx={styles.mainTitle}>QDOS Media</Typography>
              <Typography sx={styles.mainSubTitle}>Posts from QDOS</Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
              <Button sx={styles.backButton} onClick={handleBack}>
                back
              </Button>
              <Button sx={styles.backButton} onClick={handleViewTeamShare}>
                View Team Share
              </Button>
              <Button sx={styles.backButton} onClick={handleViewFanShare}>
                View Fan Share
              </Button>
            </Box>
          </Box>

          <Box sx={styles.mainContentBox}>
            {[...(posts || [])]
              .slice()
              .reverse()
              .map((el: any) => {
                const post = el?.post || {};
                const qdosItemId = post?.itemIds?.[0];
                const eventId = post?.eventId;

                return (
                  <Box
                    key={el._id}
                    onClick={() => setSelectedItem(el)}
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '0.75rem',
                      border: '1px solid #ddd',
                      borderRadius: '12px',
                      p: '1rem',
                      cursor: 'pointer',
                      transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                      '&:hover': {
                        transform: 'translateY(-2px)',
                        boxShadow: '0 6px 18px rgba(0, 0, 0, 0.08)',
                      },
                    }}
                  >
                    <Typography sx={{ fontWeight: 700 }}>
                      {post?.eventTitle || 'QDOS media'}
                    </Typography>

                    {post?.description && (
                      <Typography sx={{ color: '#555' }}>
                        {post.description}
                      </Typography>
                    )}

                    <Box>{renderMedia(el)}</Box>

                    <Box
                      sx={{
                        display: 'flex',
                        gap: '0.5rem',
                        flexWrap: 'wrap',
                      }}
                    >
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={(event) => {
                          event.stopPropagation();
                          handleAddJoy(el);
                        }}
                      >
                        Joy+
                      </Button>

                      <Button
                        variant="contained"
                        size="small"
                        onClick={(event) => {
                          event.stopPropagation();
                          handleAddToShare(qdosItemId, eventId, 'team');
                        }}
                      >
                        Team+
                      </Button>

                      <Button
                        variant="contained"
                        size="small"
                        onClick={(event) => {
                          event.stopPropagation();
                          handleAddToShare(qdosItemId, eventId, 'fan');
                        }}
                      >
                        Fan+
                      </Button>
                    </Box>
                  </Box>
                );
              })}
          </Box>
        </Box>
      )}

      <Dialog
        open={Boolean(selectedItem)}
        onClose={() => setSelectedItem(null)}
        maxWidth="md"
        fullWidth
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            px: '1rem',
            pt: '1rem',
          }}
        >
          <Typography variant="h6">
            {selectedItem?.post?.eventTitle || 'QDOS media'}
          </Typography>

          <IconButton onClick={() => setSelectedItem(null)}>
            <CloseIcon />
          </IconButton>
        </Box>

        <DialogContent>
          {selectedItem && (
            <Box>
              <Typography sx={{ mb: '1rem' }}>
                {selectedItem?.post?.description || 'No description'}
              </Typography>

              <Box sx={{ mb: '1rem' }}>{renderMedia(selectedItem, true)}</Box>

              <Box
                sx={{
                  display: 'flex',
                  gap: '0.5rem',
                  flexWrap: 'wrap',
                }}
              >
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => handleAddJoy(selectedItem)}
                >
                  Joy+
                </Button>

                <Button
                  variant="contained"
                  size="small"
                  onClick={() =>
                    handleAddToShare(
                      selectedItem?.post?.itemIds?.[0],
                      selectedItem?.post?.eventId,
                      'team',
                    )
                  }
                >
                  Team+
                </Button>

                <Button
                  variant="contained"
                  size="small"
                  onClick={() =>
                    handleAddToShare(
                      selectedItem?.post?.itemIds?.[0],
                      selectedItem?.post?.eventId,
                      'fan',
                    )
                  }
                >
                  Fan+
                </Button>
              </Box>
            </Box>
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default ThingsQdosMedia;